import React, { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import { UserPlus, Mail, Lock, User, Phone, MapPin, Building } from 'lucide-react';
import { useAuth } from '../../contexts/AuthContext';
import toast from 'react-hot-toast';
import StyledButton from '../ui/StyledButton';

const RegisterForm = () => {
  const navigate = useNavigate();
  const { register } = useAuth();
  const [formData, setFormData] = useState({
    email: '',
    password: '',
    confirmPassword: '',
    first_name: '',
    last_name: '',
    phone: '',
    address: '',
    city: '',
    postal_code: '',
    country: 'France'
  });
  const [loading, setLoading] = useState(false);
  const [errors, setErrors] = useState({});
  const [emailExistsModal, setEmailExistsModal] = useState({ isOpen: false, message: '' });

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
    // Clear error as user types
    if (errors[name]) {
      setErrors(prev => ({ ...prev, [name]: '' }));
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    // Validation (inline errors instead of toasts)
    const newErrors = {};
    if (!formData.first_name) newErrors.first_name = 'Ce champ est requis';
    if (!formData.last_name) newErrors.last_name = 'Ce champ est requis';
    if (!formData.email) newErrors.email = 'Ce champ est requis';
    if (!formData.password) newErrors.password = 'Mot de passe requis';
    if (!formData.confirmPassword) newErrors.confirmPassword = 'Confirmation requise';
    if (formData.password && formData.password.length < 6) newErrors.password = 'Le mot de passe doit contenir au moins 6 caractères';
    if (formData.password && formData.confirmPassword && formData.password !== formData.confirmPassword) newErrors.confirmPassword = 'Les mots de passe ne correspondent pas';

    if (Object.keys(newErrors).length > 0) {
      setErrors(newErrors);
      return;
    }

    setLoading(true);

    try {
      const result = await register({
        email: formData.email,
        password: formData.password,
        first_name: formData.first_name,
        last_name: formData.last_name,
        phone: formData.phone,
        address: formData.address,
        city: formData.city,
        postal_code: formData.postal_code,
        country: formData.country
      });

      if (result.success) {
        // Show modal instructing user to verify email instead of logging in
        setVerificationModal({ open: true, email: formData.email });
      } else if ((result.message && /email/i.test(result.message)) || (result.error && /email/i.test(result.error))) {
        setErrors(prev => ({ ...prev, email: result.message || result.error || 'Un utilisateur avec cet email existe déjà.' }));
      }
    } catch (error) {
      console.error('Registration error:', error);
      const message = error?.response?.data?.message || error?.message || '';
      if (/email/i.test(message) && /exist|utilis|déjà/i.test(message)) {
        setErrors(prev => ({ ...prev, email: message }));
      } else {
        // Silent fallback: show generic inline error under email
        setErrors(prev => ({ ...prev, email: "Une erreur est survenue. Veuillez réessayer." }));
      }
    } finally {
      setLoading(false);
    }
  };

  const inputClass = (hasError) => `pl-10 w-full px-4 py-3 ${hasError ? 'bg-gray-700/50 border border-red-500 focus:ring-red-500 focus:border-red-500' : 'bg-gray-700/50 border border-gray-600 focus:ring-iranian-500 focus:border-iranian-500'} rounded-lg text-white placeholder-gray-400 focus:outline-none transition-all duration-200`;
  const inputClassNoIcon = (hasError) => `w-full px-4 py-3 ${hasError ? 'bg-gray-700/50 border border-red-500 focus:ring-red-500 focus:border-red-500' : 'bg-gray-700/50 border border-gray-600 focus:ring-iranian-500 focus:border-iranian-500'} rounded-lg text-white placeholder-gray-400 focus:outline-none transition-all duration-200`;

  const [verificationModal, setVerificationModal] = useState({ open: false, email: '' });

  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-900 via-gray-800 to-iranian-900 flex items-center justify-center py-12 px-4 sm:px-6 lg:px-8 relative overflow-hidden">
      {/* Background Pattern */}
      <div className="absolute inset-0 bg-iranian-pattern opacity-20"></div>
      
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.5 }}
        className="max-w-3xl w-full space-y-8 relative z-10"
      >
        <div className="text-center">
          <div className="flex justify-center mb-5 mt-24">
            <div className="w-16 h-16 bg-gradient-to-br from-iranian-400 to-iranian-600 rounded-2xl flex items-center justify-center shadow-iranian">
              <UserPlus className="h-8 w-8 text-white" />
            </div>
          </div>
          <h2 className="text-4xl font-bold text-white mb-3">
            Créer un compte
          </h2>
          <p className="text-gray-300 text-lg">
            Rejoignez Mahja Jewelry et découvrez nos collections exclusives
          </p>
        </div>

        <form onSubmit={handleSubmit} className="bg-gray-800/90 backdrop-blur-sm rounded-2xl shadow-2xl border border-gray-700/50 p-10 space-y-6">
          {/* Name Fields */}
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-300 mb-2">
                Prénom *
              </label>
              <div className="relative">
                <User className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 h-4 w-4" />
                <input
                  type="text"
                  name="first_name"
                  value={formData.first_name}
                  onChange={handleInputChange}
                  className={inputClass(errors.first_name)}
                  placeholder="Votre prénom"
                />
              </div>
              {errors.first_name && <p className="mt-1 text-red-400 text-sm">{errors.first_name}</p>}
            </div>
            
            <div>
              <label className="block text-sm font-medium text-gray-300 mb-2">
                Nom *
              </label>
              <div className="relative">
                <User className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 h-4 w-4" />
                <input
                  type="text"
                  name="last_name"
                  value={formData.last_name}
                  onChange={handleInputChange}
                  className={inputClass(errors.last_name)}
                  placeholder="Votre nom"
                />
              </div>
              {errors.last_name && <p className="mt-1 text-red-400 text-sm">{errors.last_name}</p>}
            </div>
          </div>

          {/* Email */}
          <div>
            <label className="block text-sm font-medium text-gray-300 mb-2">
              Email *
            </label>
            <div className="relative">
              <Mail className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 h-4 w-4" />
              <input
                type="email"
                name="email"
                value={formData.email}
                onChange={handleInputChange}
                className={inputClass(errors.email)}
                placeholder="votre@email.com"
              />
            </div>
            {errors.email && <p className="mt-1 text-red-400 text-sm">{errors.email}</p>}
          </div>

          {/* Password Fields */}
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-300 mb-2">
                Mot de passe *
              </label>
              <div className="relative">
                <Lock className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 h-4 w-4" />
                <input
                  type="password"
                  name="password"
                  value={formData.password}
                  onChange={handleInputChange}
                  className={inputClass(errors.password)}
                  placeholder="Min. 6 caractères"
                />
              </div>
              {errors.password && <p className="mt-1 text-red-400 text-sm">{errors.password}</p>}
            </div>
            
            <div>
              <label className="block text-sm font-medium text-gray-300 mb-2">
                Confirmer *
              </label>
              <div className="relative">
                <Lock className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 h-4 w-4" />
                <input
                  type="password"
                  name="confirmPassword"
                  value={formData.confirmPassword}
                  onChange={handleInputChange}
                  className={inputClass(errors.confirmPassword)}
                  placeholder="Confirmer le mot de passe"
                />
              </div>
              {errors.confirmPassword && <p className="mt-1 text-red-400 text-sm">{errors.confirmPassword}</p>}
            </div>
          </div>

          {/* Phone */}
          <div>
            <label className="block text-sm font-medium text-gray-300 mb-2">
              Téléphone
            </label>
            <div className="relative">
              <Phone className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 h-4 w-4" />
              <input
                type="tel"
                name="phone"
                value={formData.phone}
                onChange={handleInputChange}
                className={inputClass(false)}
                placeholder="+33 6 12 34 56 78"
              />
            </div>
          </div>

          {/* Address Fields */}
          <div>
            <label className="block text-sm font-medium text-gray-300 mb-2">
              Adresse
            </label>
            <div className="relative">
              <MapPin className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 h-4 w-4" />
              <input
                type="text"
                name="address"
                value={formData.address}
                onChange={handleInputChange}
                className={inputClass(false)}
                placeholder="123 Rue de la Paix"
              />
            </div>
          </div>

          <div className="grid grid-cols-3 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-300 mb-2">
                Ville
              </label>
              <div className="relative">
                <Building className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 h-4 w-4" />
                <input
                  type="text"
                  name="city"
                  value={formData.city}
                  onChange={handleInputChange}
                  className={inputClass(false)}
                  placeholder="Paris"
                />
              </div>
            </div>
            
            <div>
              <label className="block text-sm font-medium text-gray-300 mb-2">
                Code postal
              </label>
              <input
                type="text"
                name="postal_code"
                value={formData.postal_code}
                onChange={handleInputChange}
                className={inputClassNoIcon(false)}
                placeholder="75001"
              />
            </div>
            
            <div>
              <label className="block text-sm font-medium text-gray-300 mb-2">
                Pays
              </label>
              <select
                name="country"
                value={formData.country}
                onChange={handleInputChange}
                className={inputClassNoIcon(false)}
              >
                <option value="France">France</option>
                <option value="Belgique">Belgique</option>
                <option value="Suisse">Suisse</option>
                <option value="Canada">Canada</option>
                <option value="Autre">Autre</option>
              </select>
            </div>
          </div>

          {/* Submit Button */}
          <StyledButton
            type="submit"
            loading={loading}
            fullWidth
            size="lg"
            icon={UserPlus}
            variant="primary"
          >
            Créer mon compte
          </StyledButton>

          {/* Login Link */}
          <div className="text-center">
            <p className="text-gray-400">
              Déjà un compte?{' '}
              <Link to="/login" className="text-iranian-400 hover:text-iranian-300 font-medium transition-colors duration-200">
                Se connecter
              </Link>
            </p>
          </div>
        </form>
      </motion.div>

      {verificationModal.open && (
        <div className="fixed inset-0 z-[9999] flex items-center justify-center">
          <div className="absolute inset-0 bg-black/60" onClick={() => setVerificationModal({ open: false, email: '' })}></div>
          <div className="relative bg-gray-800 border border-gray-700 rounded-2xl p-8 max-w-lg w-full mx-4 text-center">
            <h3 className="text-2xl font-bold text-white mb-3">Vérifiez votre email</h3>
            <p className="text-gray-300 mb-6">Un lien de vérification a été envoyé à <span className="text-yellow-400">{verificationModal.email}</span>. Ouvrez votre boîte mail et cliquez sur le lien pour activer votre compte. Ensuite, vous pourrez vous connecter.</p>
            <button onClick={() => { setVerificationModal({ open: false, email: '' }); navigate('/login'); }} className="px-5 py-2 rounded-lg bg-yellow-600 hover:bg-yellow-700 text-gray-900 font-semibold">OK</button>
          </div>
        </div>
      )}
    </div>
  );
};

export default RegisterForm;
